import {
  Component,
  ElementRef,
  Input,
  viewChild,
  SimpleChanges,
  OnChanges,
  inject,
} from '@angular/core';
import * as d3 from 'd3';
import { CommonModule } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { ConsumptionStats } from '../../../../fuel-consumption/models/fuel-consumption-details.model';

@Component({
  selector: 'app-pie-chart',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './pie-chart.component.html',
  styleUrls: ['./pie-chart.component.scss'],
})
export class PieChartComponent implements OnChanges {
  private readonly transloco = inject(TranslocoService);

  @Input() consumptionStats: ConsumptionStats[];
  
  data: any = [];
  
  private chartContainer = viewChild<ElementRef<HTMLElement>>('pieChartContainer');
  private colors = ['#E46F2C', '#FF7F00', '#FF9500', '#FFB132', '#FFE5A5'];
  private prepareData() {
    return this.consumptionStats.map((item, index) => ({
      name: item.name,
      value: item.consumption,
      color: this.colors[index % this.colors.length]
    }));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['consumptionStats']) {
      this.drawChart();
    }
  }

  private drawChart(): void {
    this.data = this.prepareData()
    const scoreLabel = this.transloco.translate('fuelShared.charts.score');
    const element = this.chartContainer();
    const width = 190;
    const height = 176;
    const margin = 10;
    const radius = Math.min(width, height) / 2 - margin;

    d3.select(element?.nativeElement as HTMLElement).selectAll('*').remove();

    let tooltip = d3.select(element?.nativeElement as HTMLElement).select('div.tooltip');
    if (tooltip.empty()) {
      d3.select(element?.nativeElement as HTMLElement)
        .append('div')
        .attr('class', 'tooltip')
        .style('opacity', 0)
        .style('min-width', '160px')
        .style('position', 'absolute')
        .style('background', '#fff')
        .style('border-radius', '8px')
        .style('padding', '8px 10px')
        .style('box-shadow', '0 4px 10px rgba(0, 0, 0, 0.08)')
        .style('border', '1px solid #e0e0e0')
        .style('pointer-events', 'none')
        .style('z-index', '10');
      tooltip = d3.select(element?.nativeElement as HTMLElement).select('div.tooltip');
    }

    const svg = d3
      .select(element?.nativeElement as HTMLElement)
      .append('svg')
      .attr('width', width)
      .attr('height', height)
      .append('g')
      .attr('transform', `translate(${radius + 16},${height / 2})`);

    const pie = d3
      .pie<any>()
      .value((d: any) => d.value)
      .sort(null);

    const data_ready = pie(this.data);

    const arc = d3
      .arc()
      .innerRadius(radius * 0.58)
      .outerRadius(radius);

    svg
      .selectAll('path')
      .data(data_ready)
      .enter()
      .append('path')
      .attr('d', <any>arc)
      .attr('fill', (d) => d.data.color)
      .attr('stroke', '#fff')
      .style('stroke-width', '0')
      .on('mouseover', (event, d) => showTooltip(event, d))
      .on('mousemove', (event) => {
        const [xVal, yVal] = d3.pointer(event, element?.nativeElement);
        tooltip.style('left', `${xVal + 10}px`).style('top', `${yVal + 10}px`);
      })
      .on('mouseout', () =>
        tooltip.transition().duration(300).style('opacity', 0)
      );

    svg
      .selectAll('text')
      .data(data_ready)
      .enter()
      .append('text')
      .text((d) => `${d.data.value}`)
      .attr('transform', (d) => `translate(${arc.centroid(d as any)})`)
      .style('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('font-weight', '700')
      .style('font-family', '"Nunito Sans", sans-serif !important')
      .style('fill', '#fff')
      .on('mouseover', (event, d) => showTooltip(event, d))
      .on('mousemove', (event) => {
        const [xVal, yVal] = d3.pointer(event, element?.nativeElement);
        tooltip.style('left', `${xVal + 10}px`).style('top', `${yVal + 10}px`);
      })
      .on('mouseout', () =>
        tooltip.transition().duration(300).style('opacity', 0)
      );

    svg
      .append('text')
      .attr('text-anchor', 'middle')
      .style('font-size', '18px')
      .style('font-weight', 'bold')
      .style('font-family', '"Nunito Sans", sans-serif !important')
      .style('fill', '#4D4749')
      .text(scoreLabel);

    function showTooltip(event: MouseEvent, d: any) {
      tooltip
        .html(
          `
            <div style="font-weight: 500; font-size: 12px; color: #2B2B2B; margin-bottom: 6px;">
              ${scoreLabel}
            </div>
            <div style="display: flex; align-items: center; margin-bottom: 4px;">
              <div style="width: 8px; height: 8px; background: ${d.data.color}; border-radius: 50%; margin-right: 6px;"></div>
              <div style="color: #9FA1A4; font-size: 10px; flex: 1;">${d.data.name}</div>
              <div style="color: #9FA1A4; font-size: 10px;">${d.data.value}</div>
            </div>
          `
        )
        .transition()
        .duration(200)
        .style('opacity', 1);
    }
  }
}